import type { ReactNode } from "react"
import Commandbar from "./Commandbar"
import { useVariantsByIds } from "~/hooks/useVariants"
import { useProductTableContext } from "~/context/ProductTableProvider"
import MultiEditDialog from "~/components/planning/MultiEditDialog/MultiEditDialog"
import { getCommonVendors } from "~/utils/vendorUtils"
import { Button } from "~/components/reusable/Button"
import { EditSkuTagsDialog } from "./Tags/EditSkuTagsDialog"

export type SelectionUnsavedChanges = {
  isDirty: boolean
  onDiscard: () => void
  onSave: () => void
  isSaving: boolean
  saveDisabled?: boolean
}

type Props = {
  selectedVariantIds: string[]
  selectedVariantCount: number
  clearSelectedIds: () => void
  unsavedChanges?: SelectionUnsavedChanges
  /** Table-specific bulk actions on the selection, e.g. resolving MOQ / colli violations. */
  renderSelectionActions?: (selectedVariantIds: string[]) => ReactNode
}

export function MultiEditSeasonalBuysPanel({
  selectedVariantIds,
  selectedVariantCount,
  clearSelectedIds,
  unsavedChanges,
  renderSelectionActions,
}: Props) {
  const { onApplySuggestedOrders } = useProductTableContext()

  const variants = useVariantsByIds(selectedVariantIds)
  const skuIds = variants.flatMap((v) => v.skuIds)
  const commonVendors = getCommonVendors(variants)

  return (
    <>
      {selectedVariantCount > 0 && (
        <Commandbar count={selectedVariantCount} onClear={clearSelectedIds}>
          {onApplySuggestedOrders && (
            <Button
              variant="neutral"
              size="sm"
              onClick={() => onApplySuggestedOrders(selectedVariantIds)}
            >
              Apply Suggested Orders
            </Button>
          )}
          {renderSelectionActions?.(selectedVariantIds)}
          <MultiEditDialog
            selectedSkuIds={skuIds}
            selectedVariantCount={selectedVariantCount}
            resetSelectedProductIds={clearSelectedIds}
            commonVendors={commonVendors}
            triggerButtonVariant="neutral"
          />
          <EditSkuTagsDialog variantIds={selectedVariantIds} />
          {unsavedChanges?.isDirty && (
            <>
              <Button
                variant="neutral"
                size="sm"
                onClick={unsavedChanges.onDiscard}
                disabled={unsavedChanges.isSaving}
              >
                Discard
              </Button>
              <Button
                size="sm"
                requires="edit"
                onClick={unsavedChanges.onSave}
                disabled={unsavedChanges.saveDisabled}
                loading={unsavedChanges.isSaving}
              >
                Save
              </Button>
            </>
          )}
        </Commandbar>
      )}
    </>
  )
}
